/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { forwardRef, RefObject, useEffect } from "react";

import styled, { css } from "styled-components";

import { Plate } from "src/classes/plate/Plate";
import { ContextMenu } from "src/components/UI/ContextMenu";
import { GRID_BACKGROUND_OFFSET, GRID_SIZE, GRID_WIDTH_EXTRA_PADDING, VIENNA_CANVAS_ID } from "src/lib/constants";

interface Props {
  plate: Plate;
  children: React.ReactNode | React.ReactNode[];
}

export const Canvas = forwardRef<any, Props>(({ plate, children }, ref) => {
  const height = plate.observe((state) => state.height);
  const width = plate.observe((state) => state.width) + GRID_WIDTH_EXTRA_PADDING;
  const editable = plate.observe((state) => state.editable);

  useEffect(() => {
    if ((ref as RefObject<HTMLDivElement>)?.current) {
      plate.mouseTracker.onCanvasMounts(ref as RefObject<HTMLDivElement>);
    }
  }, [(ref as RefObject<HTMLDivElement>)?.current]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!editable) return;
    if (e.button !== 0) return;
    // check to see if the mouse click happened on the current ref
    if (e.target !== (ref as RefObject<HTMLDivElement>)?.current) return;
    plate.onMouseDownCanvas(e);
  };

  return (
    <ContextMenu plate={plate} editable={editable} block={null}>
      <StyledCanvas
        ref={ref as RefObject<HTMLDivElement>}
        id={VIENNA_CANVAS_ID}
        width={width}
        height={height}
        onMouseDown={(e) => handleMouseDown(e)}
        showGrid={editable}
      >
        {children}
      </StyledCanvas>
    </ContextMenu>
  );
});

const StyledCanvas = styled.div<{ showGrid: boolean; width: number; height: number }>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  ${({ showGrid }) =>
    showGrid &&
    css`
      background-position: -${GRID_BACKGROUND_OFFSET}px -${GRID_BACKGROUND_OFFSET}px;
      background-size: ${`${GRID_SIZE}px`} ${`${GRID_SIZE}px`};
      ${({ theme }) =>
        css`
          background-image: radial-gradient(circle, ${theme.canvas.grid} 1px, #00000000 1px);
        `}
    `}
  @media print {
    background-image: none;
  }
`;
